import { CommandProps, Node, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { TCaptionMap } from '@shared/backendFrontend/captionHelpersFe';

import insertCaptionCommand from './insert/insertCaptionCommand';
import getTrackCaptionsPlugin from './plugins/getTrackCaptionsPlugin';
import getAutoIndexCaptionsPlugin, { type TCaptionSeed } from './plugins/getAutoIndexCaptionsPlugin';
import getCaptionZwspPlugin from './plugins/getCaptionZwspPlugin';
import { CAPTION_NODE_NAME, OutRefBookmarkKind } from './constants';
import { markSystemTr } from '../../plugins/shared';
import setupDragHandle from '../../utils/setupDragHandle';
import { MutableRefObject } from 'react';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    [CAPTION_NODE_NAME]: {
      insertCaptionCommand: () => ReturnType;
      bumpCaptionLabelNonce: () => ReturnType;
    };
  }
}

type TOptions = {
  onUpdate?: (map: TCaptionMap) => void;
  refResolveLabel: MutableRefObject<(kind: OutRefBookmarkKind) => string>;
  /**
   * Captions preceding the part the editor holds, when a large document is loaded in parts. Absent
   * for a whole document in the editor, and numbering then starts at 1 as it always did.
   */
  numberingSeed?: () => TCaptionSeed | null;
};

const CaptionNode = Node.create<TOptions>({
  name: CAPTION_NODE_NAME,
  group: 'block fieldBlock',
  content: 'text*', // disallow bold/link/ref inside caption
  defining: true,
  draggable: true, // e.g. when users drag the image node and then wanna drag the caption along

  addAttributes() {
    return {
      labelNonce: { default: 0, rendered: false }, // invisible, used only to break eq()
      bookmarkKind: {
        parseHTML: (el) => el.getAttribute('data-bookmark-kind'),
        renderHTML: (attrs) => ({ 'data-bookmark-kind': attrs.bookmarkKind }),
      },
      textAlign: {
        default: 'left',
        parseHTML: (el) => el.getAttribute('data-align'),
        renderHTML: (attrs) => ({ 'data-align': attrs.textAlign }),
      },
      // 1-based sequence number
      index: {
        default: null, // set by backend on import and updated by auto-index plugin
        parseHTML: (el) => el.getAttribute('data-index'),
        renderHTML: (attrs) => ({ 'data-index': attrs.index }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'caption-node' }];
  },

  renderHTML({ HTMLAttributes }) {
    // stored html: no prefix
    return ['caption-node', HTMLAttributes, ['span', { class: 'caption-content' }, 0]];
  },

  addNodeView() {
    const refResolveLabel = this.options.refResolveLabel;

    return ({ node }) => {
      const wrapper = document.createElement('span');
      wrapper.className = 'caption-wrapper';
      wrapper.setAttribute('data-align', node.attrs.textAlign); // for css to align
      wrapper.setAttribute('data-id', node.attrs.id); // for scrolling
      wrapper.setAttribute('data-node-view-wrapper', ''); // following tiptap convention

      const kind = node.attrs.bookmarkKind as OutRefBookmarkKind;
      wrapper.setAttribute('data-bookmark-kind', kind); // for css spacing rules

      // Prefix element: drag handle + visual label (e.g. "Figure 1: ")
      const prefix = document.createElement('span');
      prefix.className = 'caption-prefix';
      prefix.contentEditable = 'false';
      const label = refResolveLabel.current(kind);
      const idx = node.attrs.index;
      prefix.textContent = `${label} ${idx}: `;

      const content = document.createElement('span');
      content.className = 'caption-content';
      content.setAttribute('data-node-view-content', ''); // following tiptap convention

      setupDragHandle(wrapper, prefix);

      wrapper.append(prefix, content);

      return {
        dom: wrapper,
        contentDOM: content,
        // The update method says "Yes, I can keep reusing this same NodeView DOM" rather than forcing Tiptap to tear it down each time the node changes.
        update(updatedNode) {
          if (updatedNode.type !== node.type) return false;

          const newKind = updatedNode.attrs.bookmarkKind as OutRefBookmarkKind;
          wrapper.setAttribute('data-bookmark-kind', newKind);
          const newLabel = refResolveLabel.current(newKind);
          const newIdx = updatedNode.attrs.index;
          prefix.textContent = `${newLabel} ${newIdx}: `;
          wrapper.setAttribute('data-align', updatedNode.attrs.textAlign);

          return true;
        },
      };
    };
  },

  addCommands() {
    return { insertCaptionCommand, bumpCaptionLabelNonce };
  },

  addProseMirrorPlugins() {
    return [
      getTrackCaptionsPlugin(this.options.onUpdate),
      getAutoIndexCaptionsPlugin(this.options.numberingSeed), // sets attrs.index only
      getCaptionZwspPlugin(this.type), // Safari cursor fix: maintains ZWSP in empty captions
      // Safari Cmd+Backspace fix: the caption prefix is a contenteditable="false" <span>,
      // and Safari's deleteHardLineBackward can traverse across contenteditable boundaries,
      // deleting content above the caption. Intercept and scope deletion to caption text only.
      new Plugin({
        key: new PluginKey('captionSafariBackspace'),
        props: {
          handleDOMEvents: {
            beforeinput(view, event) {
              if (event.inputType !== 'deleteHardLineBackward') return false;
              const { $from } = view.state.selection;
              for (let d = $from.depth; d >= 0; d--) {
                if ($from.node(d).type.name === CAPTION_NODE_NAME) {
                  event.preventDefault();
                  const start = $from.start(d);
                  const { from } = view.state.selection;
                  if (from > start) {
                    view.dispatch(view.state.tr.delete(start, from));
                  }
                  return true;
                }
              }
              return false;
            },
          },
        },
      }),
    ];
  },
});

const bumpCaptionLabelNonce =
  () =>
  ({ state, dispatch }: CommandProps) => {
    if (!dispatch) return true;
    let tr = state.tr;
    state.doc.descendants((node, pos) => {
      if (node.type.name === CAPTION_NODE_NAME) {
        tr = tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          labelNonce: (node.attrs.labelNonce ^ 1) >>> 0, // flip 0/1
        });
      }
      return true;
    });
    dispatch(markSystemTr(tr));
    return true;
  };

export default CaptionNode;
